import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/utils/classes";
import { Progress } from "@/components/ui/progress";
import { PadlockIcon } from "@/assets/icons";
import { InfoModalTrigger } from "@/components/ui/modal/info-modal-trigger";
import { ReactNode } from "react";
import { useSegment } from "@/hooks/use-segment";

/* Variants */
const variants = cva("flex flex-row justify-between text-base items-center gap-2", {
  variants: {
    size: {
      small: "text-xs",
      medium: "text-base",
      large: "text-lg font-medium",
      xlarge: "text-xl font-medium",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

/* Prop Types */
interface MetricProps extends VariantProps<typeof variants> {
  title: string;
  value: number;

  className?: string;
  truncate?: boolean;
  locked?: boolean;
  icon?: React.ReactNode;
  infoModal?: ReactNode;

  // For segment tracking
  studyId?: string;
  projectId?: string;
  assetId?: string;
  reportType?: string;
}

/*
 * Metric Component
 * Displays the metric title and value with a progress bar
 */
export const Metric = ({ title, value, className, size, truncate, icon, locked, infoModal, studyId, projectId, assetId, reportType }: MetricProps) => {
  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleInfoModalClick = () => {
    if (studyId && projectId && assetId && reportType) {
      // Segment tracking
      segmentTrackEvent("Asset Report Tooltip Viewed", {
        name: reportType,
        value: title,
        reportType,
        studyId,
        projectId,
        assetId,
      });
    }
  };

  return (
    <div className={cn(variants({ size }), className)}>
      <div className="flex flex-row items-center gap-2">
        {icon}
        <span className={cn({ truncate: truncate })}>{title}</span>
        {infoModal && <InfoModalTrigger modalContent={infoModal} onClick={handleInfoModalClick} />}
      </div>
      <div className="flex flex-row items-center gap-2">
        <Progress
          value={value}
          className={cn("w-12 h-1.5", {
            "h-1": size === "small",
            "h-2 w-28": size === "large" || size === "xlarge",
          })}
        />
        <span className="flex flex-row w-12 justify-end">
          {locked ? (
            <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
              <PadlockIcon isGradient />
            </div>
          ) : (
            <>{value}%</>
          )}
        </span>
      </div>
    </div>
  );
};
